import React, { useEffect, useMemo, useState } from "react";

import { faMinus, faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faEyeSlash } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Icon, Spinner, Text } from "@performyard/py-components";

import InputTextArea from "../../../../../components/Inputs/InputTextArea";
import PublicNotesHeader from "./PublicNotesHeader";

import { useMeetingNotes, useUpdatePrivateMeetingNotes, useUpdatePublicMeetingNotes } from "../../../../../api/hooks";
import useAutosave from "../../../../../hooks/useAutosave";

import { useMeetingContext } from "../Meetings/MeetingContext";

const MeetingNotes = () => {
	const { meeting, userId, participantName, isInteractionDisabled, isAdminView } = useMeetingContext();
	const { data: notes, isLoading: isNotesLoading } = useMeetingNotes(meeting._id);

	// Public and private notes update functions
	const { mutate: updatePublicMeetingNotes, isError: isPublicNotesError } = useUpdatePublicMeetingNotes();
	const { mutate: updatePrivateMeetingNotes, isError: isPrivateNotesError } = useUpdatePrivateMeetingNotes();

	// Filter notes data with context-aware naming
	const notesData = useMemo(() => {
		// Get meeting creator ID and other participant ID
		const meetingCreatorId = meeting?.created?.userId;
		const otherParticipantId = meeting?.participants?.find((p) => p.userId !== meetingCreatorId)?.userId;

		// Check if current user is a participant in this meeting
		const isCurrentUserParticipant = meeting?.participants?.some((p) => p.userId === userId);

		// In admin non-participant view, we show both participants' notes
		// In regular view or admin-participant view, we show current user's notes vs other's notes
		const isAdminNonParticipant = isAdminView && !isCurrentUserParticipant;

		// Find meeting creator's public notes
		const creatorPublicNotes = notes?.find(
			(note) =>
				note.visibility === "public" &&
				note.participants.find(
					(participant) => participant.userId === meetingCreatorId && participant.role === "editor"
				)
		);

		// Find other participant's public notes
		const otherParticipantPublicNotes = notes?.find(
			(note) =>
				note.visibility === "public" &&
				note.participants.find(
					(participant) => participant.userId === otherParticipantId && participant.role === "editor"
				)
		);

		// Find current user's public notes (only relevant if they're a participant)
		const currentUserPublicNotes = notes?.find(
			(note) =>
				note.visibility === "public" &&
				note.participants.find((participant) => participant.userId === userId && participant.role === "editor")
		);

		// Find current user's private notes (always their own)
		const currentUserPrivateNotes = notes?.find(
			(note) =>
				note.visibility === "private" &&
				note.participants.find((participant) => participant.userId === userId && participant.role === "editor")
		);

		// Determine which notes to show in each tab based on context
		if (isAdminNonParticipant) {
			// Admin non-participant: show both participants' notes
			return {
				primaryPublicNotes: creatorPublicNotes,
				secondaryPublicNotes: otherParticipantPublicNotes,
				userPrivateNotes: null, // Admin non-participants can't see private notes
				isPrimaryUserNotes: false, // Primary tab shows creator's notes, not "user's" notes
				primaryNotesOwnerName:
					useMeetingContext().workspace?.participants?.find((p) => p.userId === meetingCreatorId)?.name ||
					"Meeting Owner",
				secondaryNotesOwnerName: participantName || "Participant",
			};
		} else {
			// Regular user or admin-participant: show their own notes vs other participant's notes
			const otherParticipantViewableNotes = notes?.find(
				(note) =>
					note.visibility === "public" &&
					note.participants.find(
						(participant) => participant.userId === userId && participant.role === "viewer"
					)
			);

			return {
				primaryPublicNotes: currentUserPublicNotes,
				secondaryPublicNotes: otherParticipantViewableNotes,
				userPrivateNotes: currentUserPrivateNotes,
				isPrimaryUserNotes: true, // Primary tab shows user's own notes
				primaryNotesOwnerName: "You",
				secondaryNotesOwnerName: participantName || "Participant",
			};
		}
	}, [notes, userId, isAdminView, meeting?.created?.userId, meeting?.participants, participantName]);

	// Track which notes are displayed - use semantic names
	const [showPrimaryPublicNotes, setShowPrimaryPublicNotes] = useState(true);
	const [isPrivateNotesVisible, setIsPrivateNotesVisible] = useState(false);

	// State for tracking public and private note content
	const [publicNoteContent, setPublicNoteContent] = useState("");
	const [privateNoteContent, setPrivateNoteContent] = useState("");

	// State to track if initial content has been loaded
	const [publicContentLoaded, setPublicContentLoaded] = useState(false);
	const [privateContentLoaded, setPrivateContentLoaded] = useState(false);

	// Use the centralized notes disabled logic from context
	const isNotesDisabled = isInteractionDisabled || isNotesLoading;

	// Effect to show private notes on component mount if they exist
	const hasPrivateNotes =
		!!notesData.userPrivateNotes &&
		!!notesData.userPrivateNotes.content &&
		notesData.userPrivateNotes.content.trim() !== "";

	// Check if current user is a participant in this meeting
	const isCurrentUserParticipant = meeting?.participants?.some((p) => p.userId === userId);

	// Determine if private notes should be shown (regular users or admin participants)
	const shouldShowPrivateNotes = !isAdminView || (isAdminView && isCurrentUserParticipant);

	useEffect(() => {
		if (hasPrivateNotes && shouldShowPrivateNotes) {
			setIsPrivateNotesVisible(true);
		}
	}, [hasPrivateNotes, setIsPrivateNotesVisible, shouldShowPrivateNotes]);

	// Autosave for public notes
	const {
		showSaved: showPublicNotesSaved,
		showSaving: showPublicNotesSaving,
		handleChange: handlePublicNoteChange,
		isDirty: isPublicDirty,
	} = useAutosave({
		value: publicNoteContent,
		setValue: setPublicNoteContent,
		onSave: async (content) => {
			if (notesData.primaryPublicNotes?._id) {
				updatePublicMeetingNotes({
					noteId: notesData.primaryPublicNotes._id,
					data: { content },
					meetingId: meeting._id,
				});
			}
		},
		delay: 1500,
		enabled: !!notesData.primaryPublicNotes?._id,
		minSavingTime: 1000,
	});

	// Autosave for private notes
	const {
		showSaved: showPrivateNotesSaved,
		showSaving: showPrivateNotesSaving,
		handleChange: handlePrivateNoteChange,
		isDirty: isPrivateDirty,
	} = useAutosave({
		value: privateNoteContent,
		setValue: setPrivateNoteContent,
		onSave: async (content) => {
			if (notesData.userPrivateNotes?._id) {
				updatePrivateMeetingNotes({
					noteId: notesData.userPrivateNotes._id,
					data: { content },
					meetingId: meeting._id,
				});
			}
		},
		delay: 1500,
		enabled: !!notesData.userPrivateNotes?._id && isPrivateNotesVisible,
		minSavingTime: 1000,
	});

	// Initialize note content from API data
	useEffect(() => {
		// Handle public notes initial load
		if (
			!isNotesLoading &&
			notesData.primaryPublicNotes?.content !== undefined &&
			!publicContentLoaded &&
			!isPublicDirty
		) {
			setPublicNoteContent(notesData.primaryPublicNotes.content);
			setPublicContentLoaded(true);
		}

		// Handle private notes initial load
		if (
			!isNotesLoading &&
			notesData.userPrivateNotes?.content !== undefined &&
			!privateContentLoaded &&
			!isPrivateDirty
		) {
			setPrivateNoteContent(notesData.userPrivateNotes.content);
			setPrivateContentLoaded(true);
		}
	}, [
		isNotesLoading,
		notesData.primaryPublicNotes,
		publicContentLoaded,
		isPublicDirty,
		setPublicNoteContent,
		setPublicContentLoaded,
		notesData.userPrivateNotes,
		privateContentLoaded,
		isPrivateDirty,
		setPrivateNoteContent,
		setPrivateContentLoaded,
	]);

	// Handler for public notes dropdown option selection
	const handlePublicNotesOptionSelect = (option: { id: string; label: string }) => {
		setShowPrimaryPublicNotes(option.id === "my-notes");
	};

	// Determine placeholder text based on context and whose notes are being shown
	const getPlaceholderText = () => {
		if (showPrimaryPublicNotes) {
			// Primary notes tab
			if (notesData.isPrimaryUserNotes) {
				return "Your notes here";
			} else {
				return `${notesData.primaryNotesOwnerName} hasn't added any notes`;
			}
		}
		// Secondary notes tab
		return `${notesData.secondaryNotesOwnerName} hasn't added any notes`;
	};

	return (
		<div className="space-y-2">
			<div className="space-y-4">
				<div className="relative space-y-2">
					<PublicNotesHeader
						showMyPublicNotes={showPrimaryPublicNotes}
						showPublicNotesSaved={showPublicNotesSaved}
						showPublicNotesSaving={showPublicNotesSaving}
						onOptionSelect={handlePublicNotesOptionSelect}
						isPublicNotesError={isPublicNotesError}
					/>

					{showPrimaryPublicNotes ? (
						<InputTextArea
							value={publicNoteContent}
							onChange={handlePublicNoteChange}
							placeholder={getPlaceholderText()}
							minRows={8}
							maxRows={8}
							disabled={isNotesDisabled || (isAdminView && !notesData.isPrimaryUserNotes)}
							className={
								isAdminView && !notesData.isPrimaryUserNotes ? "!bg-neutral-1 !text-neutral-9" : ""
							}
						/>
					) : (
						<InputTextArea
							value={notesData.secondaryPublicNotes?.content || ""}
							placeholder={getPlaceholderText()}
							minRows={8}
							maxRows={8}
							disabled={true}
							className="!bg-neutral-1 !text-neutral-9"
							onChange={() => null}
						/>
					)}
				</div>

				{notesData.userPrivateNotes && isPrivateNotesVisible && shouldShowPrivateNotes && (
					<div className="space-y-2">
						<div className="relative space-y-2">
							<div className="flex items-center justify-between space-x-1">
								<div className="flex items-center space-x-1">
									<Icon size={16} icon={faEyeSlash} />
									<Text size="paragraph-3" weight="semi-bold">
										My Private Notes
									</Text>
								</div>
								<div className="flex h-[20px] min-w-[60px] items-center justify-end overflow-hidden">
									{showPrivateNotesSaving && !isPrivateNotesError && (
										<div className="flex items-center space-x-2 text-[10px] text-neutral-9">
											<Text size="extra-small" weight="regular">
												Saving
											</Text>
											<Spinner size={12} />
										</div>
									)}
									{showPrivateNotesSaved && !isPrivateNotesError && (
										<div className="flex items-center text-[10px] text-neutral-9">
											<Text size="extra-small" weight="regular">
												Saved
											</Text>
										</div>
									)}
								</div>
							</div>
							<InputTextArea
								value={privateNoteContent}
								onChange={handlePrivateNoteChange}
								placeholder="Your notes here"
								minRows={8}
								maxRows={8}
								disabled={isNotesDisabled}
							/>
						</div>
						<div className="w-fit">
							<ButtonStandard
								iconLeft={faMinus}
								size="medium"
								color="red"
								onClick={() => setIsPrivateNotesVisible(false)}
								disabled={isNotesLoading}
							>
								Hide Private Notes
							</ButtonStandard>
						</div>
					</div>
				)}

				{notesData.userPrivateNotes !== null && !isPrivateNotesVisible && shouldShowPrivateNotes && (
					<div className="w-fit">
						<ButtonStandard
							iconLeft={faPlus}
							size="medium"
							onClick={() => setIsPrivateNotesVisible(true)}
							disabled={isNotesLoading || (isInteractionDisabled && !hasPrivateNotes)}
							dangerouslySetClassName={
								isNotesLoading || (isInteractionDisabled && !hasPrivateNotes)
									? "disabled:cursor-not-allowed disabled:hover:cursor-not-allowed disabled:pointer-events-auto disabled:hover:bg-primary-5 disabled:active:bg-primary-5"
									: ""
							}
						>
							{hasPrivateNotes ? "Show Private Notes" : "Add Private Notes"}
						</ButtonStandard>
					</div>
				)}
			</div>
		</div>
	);
};

export default MeetingNotes;
